import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useBranch } from '../contexts/BranchContext';
import api from '../utils/api';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { CalendarBlank, Clock, Users, Bed, Warning, CheckCircle, BellRinging } from '@phosphor-icons/react';
import { toast } from 'sonner';
import ClientTrackingModal from '../components/ClientTrackingModal';

// Convert 24-hour time to 12-hour AM/PM format
const convertTo12Hour = (time24) => {
  if (!time24) return '';
  
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

const BatchSchedule = () => {
  const { activeBranch } = useBranch();
  const [schedule, setSchedule] = useState([]);
  const [alerts, setAlerts] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBedLayout, setShowBedLayout] = useState(false);
  const [alertsShown, setAlertsShown] = useState(false);
  const [showClientTracking, setShowClientTracking] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedBatchTime, setSelectedBatchTime] = useState(null);

  useEffect(() => {
    if (activeBranch) {
      fetchSchedule();
      const interval = setInterval(fetchSchedule, 30000);
      return () => clearInterval(interval);
    }
  }, [activeBranch]);

  const fetchSchedule = async () => {
    try {
      const response = await api.get('/batches/schedule', {
        params: { branch_id: activeBranch?.branch_id }
      });
      setSchedule(response.data.batches || []);
      setAlerts(response.data.alerts || null);
      
      // Show alerts only once when page loads
      if (!alertsShown && response.data.alerts) {
        const { full_count, warning_count, current_batch_full, current_batch_warning } = response.data.alerts;
        
        if (current_batch_full) {
          toast.error('🚨 Current batch is at 100% capacity!', {
            duration: 5000,
            description: 'No beds available in the active batch'
          });
        } else if (current_batch_warning) {
          toast.warning('⚠️ Current batch is at 80%+ capacity', {
            duration: 4000,
            description: 'Limited beds available'
          });
        }
        
        if (full_count > 0) {
          toast.error(`${full_count} batch${full_count > 1 ? 'es' : ''} at full capacity`, {
            duration: 4000
          });
        }
        
        setAlertsShown(true);
      }
    } catch (error) {
      toast.error('Failed to load batch schedule');
    } finally {
      setLoading(false);
    }
  };

  const viewBedLayout = (batch) => {
    setSelectedBatch(batch);
    setShowBedLayout(true);
  };

  const handleBedClick = (bed) => {
    // Only open tracking for occupied beds
    if (bed.status === 'occupied' && bed.customer) {
      setSelectedCustomer(bed.customer);
      setSelectedBatchTime(selectedBatch?.start_time);
      setShowClientTracking(true);
    }
  };

  const BedLayoutGrid = ({ bedLayout, capacity = 15 }) => {
    const beds = [];
    for (let i = 1; i <= capacity; i++) {
      beds.push(i);
    }

    return (
      <div className="grid grid-cols-5 gap-3">
        {beds.map((bedNum) => {
          const occupied = bedLayout[bedNum];
          const isOccupied = !!occupied;
          
          return (
            <div
              key={bedNum}
              onClick={() => isOccupied && handleBedClick({ 
                bed_number: bedNum, 
                status: 'occupied', 
                customer: occupied 
              })}
              className={`relative aspect-square rounded-lg border-2 flex flex-col items-center justify-center p-2 transition-all ${
                isOccupied
                  ? 'bg-red-100 border-red-400 cursor-pointer hover:shadow-lg hover:scale-105'
                  : 'bg-green-100 border-green-400'
              }`}
              title={isOccupied ? `Click to view ${occupied.customer_name}'s tracking` : ''}
            >
              <Bed size={24} className={isOccupied ? 'text-red-600' : 'text-green-600'} weight="fill" />
              <div className="text-sm font-bold mt-1">{bedNum}</div>
              {isOccupied && (
                <div className="absolute inset-0 flex items-center justify-center bg-red-600/10 rounded-lg pointer-events-none">
                  <div className="text-center">
                    <div className="text-xs font-medium text-red-900">Token #{occupied.token_number}</div>
                    <div className="text-[10px] text-red-700 mt-1">Click to track</div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">Loading schedule...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="batch-schedule-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-[#2C4C3B]">Batch Schedule</h1>
            <p className="text-[#1C1C1A]/60">Real-time bed occupancy tracking</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-400 rounded"></div>
              <span className="text-sm">Available</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-red-400 rounded"></div>
              <span className="text-sm">Occupied</span>
            </div>
          </div>
        </div>

        {/* Capacity Alerts Banner */}
        {alerts && (alerts.full_count > 0 || alerts.warning_count > 0) && (
          <Card className={`p-4 ${alerts.full_count > 0 ? 'bg-red-50 border-red-300' : 'bg-orange-50 border-orange-200'}`}>
            <div className="flex items-start gap-3">
              <BellRinging size={24} className={`${alerts.full_count > 0 ? 'text-red-600' : 'text-orange-600'} flex-shrink-0 mt-1`} weight="fill" />
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <h3 className={`font-semibold ${alerts.full_count > 0 ? 'text-red-900' : 'text-orange-900'}`}>
                    Capacity Alerts
                  </h3>
                  <div className="flex gap-3 text-sm">
                    {alerts.full_count > 0 && (
                      <span className="px-3 py-1 bg-red-200 text-red-900 rounded-full font-medium">
                        {alerts.full_count} Full
                      </span>
                    )}
                    {alerts.warning_count > 0 && (
                      <span className="px-3 py-1 bg-orange-200 text-orange-900 rounded-full font-medium">
                        {alerts.warning_count} High Capacity
                      </span>
                    )}
                  </div>
                </div>
                <div className="space-y-1 text-sm">
                  {schedule.filter(b => b.alert_level).map((batch) => (
                    <div key={batch.batch_id} className="flex items-center gap-2">
                      <span className="font-medium">{convertTo12Hour(batch.start_time)}:</span>
                      <span className={batch.alert_level === 'full' ? 'text-red-700 font-bold' : 'text-orange-700'}>
                        {batch.occupied}/{batch.capacity} beds ({batch.occupancy_percent}%)
                        {batch.alert_level === 'full' ? ' - NO BEDS AVAILABLE' : ' - LIMITED AVAILABILITY'}
                      </span>
                      {batch.is_current && (
                        <Badge className="bg-green-600 text-white text-xs ml-2">Active Now</Badge>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Batch Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {schedule.map((batch) => (
            <Card
              key={batch.batch_id}
              className={`p-6 cursor-pointer hover:shadow-lg transition-all ${
                batch.is_current ? 'border-2 border-green-500 bg-green-50' : ''
              }`}
              onClick={() => viewBedLayout(batch)}
              data-testid={`batch-card-${batch.batch_id}`}
            >
              {/* Header */}
              <div className="flex justify-between items-start mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Clock size={20} className="text-[#2C4C3B]" />
                    <span className="text-lg font-bold text-[#2C4C3B]">{convertTo12Hour(batch.start_time)}</span>
                  </div>
                  <p className="text-xs text-[#1C1C1A]/60">to {convertTo12Hour(batch.end_time)}</p>
                </div>
                {batch.is_current && (
                  <Badge className="bg-green-600 text-white">
                    Now Serving
                  </Badge>
                )}
                {batch.alert_level === 'full' && (
                  <Badge variant="destructive">FULL</Badge>
                )}
                {batch.alert_level === 'warning' && (
                  <Badge className="bg-orange-500 text-white">80%</Badge>
                )}
              </div>

              {/* Occupancy */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <Users size={18} className="text-[#2C4C3B]" />
                    <span className="text-sm font-medium">Occupancy</span>
                  </div>
                  <span className="text-2xl font-bold text-[#2C4C3B]">
                    {batch.occupied}/{batch.capacity}
                  </span>
                </div>

                {/* Progress Bar */}
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      batch.occupancy_percent >= 100
                        ? 'bg-red-600'
                        : batch.occupancy_percent >= 80
                        ? 'bg-orange-500'
                        : 'bg-green-500'
                    }`}
                    style={{ width: `${Math.min(batch.occupancy_percent, 100)}%` }}
                  ></div>
                </div>

                <div className="text-xs text-center text-[#1C1C1A]/60">
                  {batch.available} beds available
                </div>
              </div>

              {/* Mini Bed Preview */}
              <div className="mt-4 pt-4 border-t border-[#E5E5DF]">
                <div className="grid grid-cols-5 gap-1">
                  {[...Array(15)].map((_, i) => {
                    const bedNum = i + 1;
                    const occupied = batch.bed_layout[bedNum];
                    return (
                      <div
                        key={bedNum}
                        className={`aspect-square rounded ${
                          occupied ? 'bg-red-400' : 'bg-green-400'
                        }`}
                        title={occupied ? `Bed ${bedNum}: ${occupied.customer_name}` : `Bed ${bedNum}: Available`}
                      ></div>
                    );
                  })}
                </div>
                <p className="text-xs text-center text-[#1C1C1A]/60 mt-2">
                  Click to view full bed layout
                </p>
              </div>
            </Card>
          ))}
        </div>

        {schedule.length === 0 && (
          <Card className="p-12 text-center">
            <CalendarBlank size={64} className="mx-auto mb-4 text-[#1C1C1A]/20" />
            <h3 className="text-xl font-semibold mb-2">No Batches Scheduled</h3>
            <p className="text-[#1C1C1A]/60 mb-4">Generate batches for today to get started</p>
          </Card>
        )}

        {/* Bed Layout Modal */}
        <Dialog open={showBedLayout} onOpenChange={setShowBedLayout}>
          <DialogContent className="bg-white max-w-3xl">
            <DialogHeader>
              <DialogTitle className="text-2xl font-semibold text-[#2C4C3B]">
                Bed Layout - Batch {convertTo12Hour(selectedBatch?.start_time)}
              </DialogTitle>
            </DialogHeader>
            
            {selectedBatch && (
              <div className="space-y-6">
                {/* Status Summary */}
                <div className="flex justify-around p-4 bg-[#F9F9F8] rounded-lg">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-600">{selectedBatch.available}</div>
                    <div className="text-sm text-[#1C1C1A]/60">Available</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-red-600">{selectedBatch.occupied}</div>
                    <div className="text-sm text-[#1C1C1A]/60">Occupied</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-[#2C4C3B]">{selectedBatch.capacity}</div>
                    <div className="text-sm text-[#1C1C1A]/60">Total Beds</div>
                  </div>
                </div>

                {/* Bed Grid */}
                <BedLayoutGrid bedLayout={selectedBatch.bed_layout} capacity={selectedBatch.capacity} />

                {/* Customer List */}
                {selectedBatch.occupied > 0 && (
                  <div className="pt-4 border-t border-[#E5E5DF]">
                    <h4 className="font-semibold mb-3">Current Occupants:</h4>
                    <div className="space-y-2">
                      {selectedBatch.customers.map((customer) => (
                        <div
                          key={customer.attendance_id}
                          className="flex justify-between items-center p-2 bg-[#F9F9F8] rounded"
                        >
                          <div>
                            <span className="font-medium">{customer.customer_name}</span>
                            <span className="text-xs text-[#1C1C1A]/60 ml-2">
                              (Token #{customer.token_number})
                            </span>
                          </div>
                          {customer.bed_number && (
                            <Badge className="bg-blue-100 text-blue-700">
                              Bed #{customer.bed_number}
                            </Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Client Tracking Modal */}
        <ClientTrackingModal
          open={showClientTracking}
          onClose={() => {
            setShowClientTracking(false);
            setSelectedCustomer(null);
            setSelectedBatchTime(null);
          }}
          customer={selectedCustomer}
          batchTime={selectedBatchTime}
        />
      </div>
    </Layout>
  );
};

export default BatchSchedule;
